import * as React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Card, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import {
  Download,
  RefreshCw,
  Banknote,
  Search,
  Calendar,
  Eye,
  Edit,
  Edit3,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import AntDTable from "@/common/components/ui/antd-table";
import { GoldService } from "@/services/APIs";
import { useToast } from "@/hooks/use-toast";
import { SettlementListData } from "@/utils/types/Gold";
import { useNavigate } from "react-router-dom";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";
import CommonModal from "@/common/components/ui/modal";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";

const PAYMENT_METHOD_OPTIONS = [
  "NEFT",
  "RTGS",
  "Bank Transfer",
  "Cheque",
  "UPI",
] as const;
const PAYMENT_STATUS_OPTIONS = ["Pending", "Paid"] as const;
const SETTLEMENT_STATUS_OPTIONS = ["Pending", "Settled"] as const;

const GoldSettlements = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState<SettlementListData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<SettlementListData[]>([]);

  // Bulk update states
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState<string>("");
  const [bulkPaymentStatus, setBulkPaymentStatus] = useState<string>("");
  const [bulkSettlementStatus, setBulkSettlementStatus] = useState<string>("");

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const response = await GoldService.settlementList(
        currentPage,
        pageSize,
        searchTerm
      );
      const resData = response as any;

      if (resData?.results) {
        setData(resData.results);
        setTotalCount(resData.count ?? 0);
      } else if (resData?.data?.results) {
        setData(resData.data.results);
        setTotalCount(resData.data.count ?? 0);
      } else if (resData?.data) {
        setData(resData.data);
      }
    } catch (_error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch settlements",
      });
    } finally {
      setLoading(false);
    }
  };

  const onSelectChange = (keys: React.Key[], rows: SettlementListData[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      "Order ID": item.order_id ?? "-",
      Customer: item.customer ?? "-",
      "Order Date": item.order_date
        ? new Date(item.order_date).toLocaleDateString()
        : "-",
      Subtotal: item.subtotal ?? "0",
      GST: item.gst_amount ?? "0",
      "Grand Total": item.grand_total ?? "0",
      "FinEase Commission": item.commission_fin_ease ?? "0",
      "Net To MMTC": item.net_to_mmtc ?? "0",
      "Settlement Amount": item.settlement_amount ?? "0",
      "Settlement Status": item.settlement_status ?? "-",
      "Payment Method": item.payment_method ?? "-",
      "Payment Status": item.payment_status ?? "-",
      "Order Status": item.order_status?.replace("_", " ") ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Settlements");

    XLSX.writeFile(
      wb,
      `gold_settlements_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} settlement(s) exported.`);
  };

  const handleBulkUpdate = async () => {
    if (selectedRowKeys.length === 0) {
      return;
    }

    if (!bulkPaymentMethod && !bulkPaymentStatus && !bulkSettlementStatus) {
      notify.warning(
        "No Changes",
        "Please select at least one field to update."
      );
      return;
    }

    setIsBulkUpdating(true);
    try {
      const payload: any = {};
      if (bulkPaymentMethod) {
        payload.payment_method =
          bulkPaymentMethod === "reset" ? null : bulkPaymentMethod;
      }
      if (bulkPaymentStatus) {
        payload.payment_status = bulkPaymentStatus.toUpperCase();
      }
      if (bulkSettlementStatus) {
        payload.settlement_status = bulkSettlementStatus.toUpperCase();
      }

      const promises = selectedRowKeys.map(id =>
        GoldService.updateSettlementById(id.toString(), payload)
      );

      const results = await Promise.allSettled(promises);
      const successful = results.filter(r => r.status === "fulfilled").length;
      const failed = results.filter(r => r.status === "rejected").length;

      if (successful > 0) {
        notify.success(
          "Update Complete",
          `Successfully updated ${successful} record(s).${failed > 0 ? ` Failed to update ${failed} record(s).` : ""}`
        );
        fetchSettlements();
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setIsBulkModalOpen(false);
        // Reset selections
        setBulkPaymentMethod("");
        setBulkPaymentStatus("");
        setBulkSettlementStatus("");
      } else {
        notify.error(
          "Update Failed",
          "None of the selected records were updated."
        );
      }
    } catch (_err) {
      notify.error("Error", "An error occurred during bulk update.");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSettlements();
    }, 400);
    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchTerm]);

  const columns = [
    {
      title: "Order ID",
      key: "order_id",
      render: (_: any, record: SettlementListData) => (
        <div className="flex flex-col gap-1">
          <span className="font-bold text-secondary">
            {record.order_id ?? "-"}
          </span>
          <span className="text-[10px] text-muted-foreground">
            MMTC: {record.mmtcid ?? "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Date",
      key: "date",
      render: (_: any, record: SettlementListData) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">
            {record.order_date
              ? new Date(record.order_date).toLocaleDateString()
              : "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Values",
      key: "values",
      align: "center",
      render: (_: any, record: SettlementListData) => (
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Order Total:</span>
            <span className="font-bold text-slate-700">
              ₹{parseFloat(record.grand_total ?? "0").toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Comm:</span>
            <span className="font-bold text-amber-600">
              ₹{parseFloat(record.commission_fin_ease ?? "0").toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between gap-4 border-t border-slate-100 pt-1">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
              Net MMTC
            </span>
            <span className="font-black text-emerald-700">
              ₹
              {parseFloat(
                record.net_to_mmtc ?? record.settlement_amount ?? "0"
              ).toLocaleString()}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Settlement Status",
      key: "settlement_status",
      align: "center",
      render: (_: any, record: SettlementListData) => {
        const isSettled =
          record.settlement_status?.toLowerCase() === "settled" ||
          record.settlement_status?.toLowerCase() === "done";
        return (
          <Badge
            variant="outline"
            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${
              isSettled
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
            }`}
          >
            {record.settlement_status || "PENDING"}
          </Badge>
        );
      },
    },
    {
      title: "Order Status",
      key: "order_status",
      align: "center",
      render: (_: any, record: SettlementListData) => {
        const orderStatusStr = record.order_status?.toUpperCase() || "NEW";
        if (orderStatusStr.includes("DELIVER")) {
          return (
            <Badge
              variant="outline"
              className="bg-green-100 text-[10px] font-bold text-green-700 hover:bg-green-200"
            >
              {orderStatusStr.replace("_", " ")}
            </Badge>
          );
        }
        return (
          <Badge
            variant="outline"
            className="bg-slate-100 text-[10px] font-bold text-slate-700 hover:bg-slate-200"
          >
            {orderStatusStr.replace("_", " ")}
          </Badge>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 140,
      render: (_: any, record: SettlementListData) => (
        <div className="flex items-center justify-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              navigate(
                finEaseHomeNavigationRoutes.PHYSICAL_GOLD_SETTLEMENT_DETAILS(
                  record.id
                )
              )
            }
            title="View Settlement"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              navigate(
                `${finEaseHomeNavigationRoutes.PHYSICAL_GOLD_SETTLEMENT_DETAILS(
                  record.id
                )}?mode=edit`
              )
            }
            title="Edit Settlement"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ] as any;

  return (
    <>
      <div className="space-y-6 p-6 pb-20">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
          <svg
            className="absolute right-0 top-0 h-full w-1/3 text-white/5"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
          </svg>
          <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                <Banknote className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold text-white">
                  Gold Settlements
                </h1>
                <p className="text-white/70">
                  Manage and review settlement calculations for gold
                  transactions
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={fetchSettlements}
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />{" "}
                Sync Data
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-white/20 bg-emerald-500/20 text-white hover:bg-emerald-500/30"
                onClick={() => setIsBulkModalOpen(true)}
                disabled={selectedRowKeys.length === 0}
              >
                <Edit3 className="h-4 w-4" />
                Bulk Update
                {selectedRows.length > 0 && (
                  <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                    {selectedRows.length}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={handleExport}
                disabled={selectedRows.length === 0}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </div>

        <Card className="border-none shadow-sm">
          <CardContent className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by Order ID or MMTC ID..."
                className="border-gray-100 bg-gray-50/50 pl-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </CardContent>
        </Card>

        <AntDTable
          showSettings
          columns={columns}
          data={data}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      <CommonModal
        isOpen={isBulkModalOpen}
        onClose={() => !isBulkUpdating && setIsBulkModalOpen(false)}
        width="max-w-md"
        padding="p-0"
      >
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-secondary to-primary/90 p-6 text-white">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur-md">
                <Edit3 className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Bulk Update Settlements</h3>
                <p className="text-xs text-white/70">
                  Modifying {selectedRowKeys.length} records simultaneously
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 p-6">
            <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-100">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="text-[13px] font-semibold text-amber-900">
                    Important Note
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-amber-700/80">
                    Leave a field empty if you do not want to modify it. Values
                    selected below will be applied to all{" "}
                    {selectedRowKeys.length} selected items.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Payment Method
                </Label>
                <Select
                  value={bulkPaymentMethod}
                  onValueChange={setBulkPaymentMethod}
                >
                  <SelectTrigger className="h-11 border-slate-200 bg-slate-50/50 shadow-sm transition-all focus:ring-2 focus:ring-primary/20">
                    <SelectValue placeholder="Select Payment Method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reset">
                      Clear Value (Set to None)
                    </SelectItem>
                    {PAYMENT_METHOD_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Payment Status
                </Label>
                <Select
                  value={bulkPaymentStatus}
                  onValueChange={setBulkPaymentStatus}
                >
                  <SelectTrigger className="h-11 border-slate-200 bg-slate-50/50 shadow-sm transition-all focus:ring-2 focus:ring-primary/20">
                    <SelectValue placeholder="Select Payment Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Settlement Status
                </Label>
                <Select
                  value={bulkSettlementStatus}
                  onValueChange={setBulkSettlementStatus}
                >
                  <SelectTrigger className="h-11 border-slate-200 bg-slate-50/50 shadow-sm transition-all focus:ring-2 focus:ring-primary/20">
                    <SelectValue placeholder="Select Settlement Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {SETTLEMENT_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setIsBulkModalOpen(false)}
                disabled={isBulkUpdating}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 gap-2 bg-secondary text-white shadow-lg shadow-secondary/20 hover:bg-secondary/90"
                onClick={handleBulkUpdate}
                disabled={
                  isBulkUpdating ||
                  (!bulkPaymentMethod &&
                    !bulkPaymentStatus &&
                    !bulkSettlementStatus)
                }
              >
                {isBulkUpdating ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Apply Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default GoldSettlements;
